import React from 'react';
import { legacyInjectIntl } from 'js/wrapper/injectIntl';
import { Overlay, Tooltip } from 'react-bootstrap';
import FormGooglePlacesAutocompleteList from './FormGooglePlacesAutocompleteList';

class FormGooglePlacesAutocomplete extends React.Component {
  constructor(props) {
    super(props);

    const coordinates = localStorage.getItem('location') || '';
    const locationAddress = localStorage.getItem('locationAddress') || '';

    this.state = {
      coordinates,
      address: locationAddress,
      googlePlacesResult: null,
      addressInputValue: locationAddress,
      listOverlayShow: false,
      prevSuggestions: [],
      showBrowserGeolocationTooltip: false,
      browserGeolocationPending: false,
      suggestions: [],
      loading: false,
    };

    this.inputWrapperRef = React.createRef();
    this.geoLocationTootipTargetRef = React.createRef();
    this.autocompleteService = null;
  }

  componentDidMount = () => {
    const { onSelect, enableBrowserLocation, autoBrowserLocation } = this.props;
    const { address, coordinates, googlePlacesResult } = this.state;
    this.initAutocompleteService();

    if (!coordinates && enableBrowserLocation && autoBrowserLocation) {
      this.setCoordsFromBrowserGeolocation();
    }

    if (coordinates) {
      onSelect(address, coordinates, googlePlacesResult);
    }
  };

  componentDidUpdate = (prevProps, prevState) => {
    const { onSelect } = this.props;
    const { address, coordinates, googlePlacesResult, addressInputValue } = this.state;
    this.initAutocompleteService();

    if (prevState.addressInputValue !== addressInputValue) {
      this.fetchPredictions(addressInputValue);
    }

    if (address !== prevState.address || coordinates !== prevState.coordinates) {
      onSelect(address, coordinates, googlePlacesResult);
    }
  };

  componentWillUnmount = () => {
    clearTimeout(this.typeTimeout);
  };

  initAutocompleteService = () => {
    if (!this.autocompleteService && typeof window !== 'undefined' && window.google && window.google.maps && window.google.maps.places) {
      this.autocompleteService = new window.google.maps.places.AutocompleteService();
    }
  };

  fetchPredictions = (value) => {
    clearTimeout(this.typeTimeout);

    if (!value || value.trim() === '') {
      this.setState({ suggestions: [], loading: false });
      return;
    }

    this.typeTimeout = setTimeout(() => {
      this.initAutocompleteService();

      if (!this.autocompleteService) {
        console.warn('Google Maps Places SDK noch nicht verfügbar.');
        return;
      }

      this.setState({ loading: true });

      this.autocompleteService.getPlacePredictions(
        { input: value },
        (predictions, status) => {
          this.setState({ loading: false });
          if (status === 'OK' && predictions) {
            const formattedSuggestions = predictions.map(p => ({
              id: p.place_id,
              placeId: p.place_id,
              description: p.description,
              active: false,
              formattedSuggestion: {
                mainText: p.structured_formatting?.main_text || '',
                secondaryText: p.structured_formatting?.secondary_text || ''
              }
            }));
            this.setState({ suggestions: formattedSuggestions });
          } else {
            this.setState({ suggestions: [] });
          }
        }
      );
    }, 350);
  };

  onFocus = () => {
    this.setState({ listOverlayShow: true, addressInputFocused: true }, () => {
      this.fetchPredictions(this.state.addressInputValue);
    });
  };

  onBlur = () => {
    const { suggestions } = this.state;
    const stateUpdate = { addressInputFocused: false };
    if (suggestions && suggestions.length) {
      stateUpdate.prevSuggestions = suggestions;
    }
    this.setState(stateUpdate);
  };

  setCoordsFromBrowserGeolocation = () => {
    const { address } = this.state;

    if (address || !navigator.geolocation) {
      return;
    }

    this.setState({ browserGeolocationPending: true }, () => {
      navigator.geolocation.getCurrentPosition(position => {
        this.setState({
          coordinates: position.coords.latitude + ',' + position.coords.longitude,
          browserGeolocationPending: false,
        });
      });
    });
  };

  onAddressInputChange = addressInputValue => {
    const { listOverlayShow } = this.state;

    const changeState = { addressInputValue };
    if (!listOverlayShow) {
      changeState.listOverlayShow = true;
    }

    this.setState(changeState);
  };

  // Uses Geocoder to resolve the geographic coordinates based on the Place ID.
  onSelect = (locationAddress, placeId) => {
    this.setState({ listOverlayShow: false }, () => {
      if (typeof window === 'undefined' || !window.google || !window.google.maps) return;

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ placeId: placeId }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const result = results[0];
          const latLng = {
            lat: result.geometry.location.lat(),
            lng: result.geometry.location.lng()
          };
          this.setPosDetectResult(result, latLng);
        }
      });
    });
  };

  setPosDetectResult = (googlePlacesResult, latLng) => {
    this.setState({
      address: googlePlacesResult.formatted_address,
      addressInputValue: googlePlacesResult.formatted_address,
      coordinates: latLng.lat + ',' + latLng.lng,
      googlePlacesResult,
    });
    localStorage.setItem('locationAddress', googlePlacesResult.formatted_address);
    localStorage.setItem('location', latLng.lat + ',' + latLng.lng);
  };

  getOverlayTarget = () => {
    return this.inputWrapperRef.current;
  };

  getOverlayWidth = () => {
    return this.inputWrapperRef.current ? this.inputWrapperRef.current.offsetWidth : 0;
  };

  // Creates a dummy function, as getSuggestionItemProps is removed in the old package.
  getSuggestionItemProps = (suggestion, customProps = {}) => {
    return {
      ...customProps,
      onClick: (e) => {
        if (customProps.onClick) customProps.onClick(e);
        this.onSelect(suggestion.description, suggestion.placeId);
      }
    };
  };

  render() {
    const {
      intl: { messages },
      name,
      placeholder,
      enableBrowserLocation,
      autoBrowserLocation,
      prevMapSearches,
    } = this.props;
    const {
      addressInputValue,
      addressInputFocused,
      listOverlayShow,
      prevSuggestions,
      showBrowserGeolocationTooltip,
      browserGeolocationPending,
      suggestions,
      loading
    } = this.state;

    const showBrowserGeolocationOption = enableBrowserLocation && !autoBrowserLocation && navigator.geolocation;

    return (
      <React.Fragment>
        <div
          ref={this.inputWrapperRef}
          className={
            (showBrowserGeolocationOption ? 'input-group ' : 'form-group form-group-default ') +
            'transparent ' +
            (addressInputFocused || listOverlayShow ? 'focused ' : '')
          }
        >
          <input
            className={'form-control ' + (!addressInputValue ? 'text-master-light ' : '')}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            placeholder={placeholder}
            name={name}
            value={addressInputValue}
            onChange={e => this.onAddressInputChange(e.target.value)}
            style={showBrowserGeolocationOption ? { borderRight: 0 } : {}}
          />

          {showBrowserGeolocationOption ? (
            <span
              ref={this.geoLocationTootipTargetRef}
              className="input-group-addon"
              onMouseOver={() => this.setState({ showBrowserGeolocationTooltip: true })}
              onMouseOut={() => this.setState({ showBrowserGeolocationTooltip: false })}
            >
              {browserGeolocationPending ? (
                <i className="fa fa-circle-notch fa-spin" />
              ) : (
                <i className="fa fa-fw fa-compass cursor" onClick={this.setCoordsFromBrowserGeolocation} />
              )}
            </span>
          ) : null}
        </div>

        <Overlay
          show={listOverlayShow}
          rootClose
          onHide={() => {
            if (this.state.addressInputFocused) {
              return;
            }
            this.setState({ listOverlayShow: false });
          }}
          placement="bottom"
          target={this.getOverlayTarget}
        >
          <FormGooglePlacesAutocompleteList
            width={this.getOverlayWidth()}
            searchTerm={addressInputValue}
            prevSuggestions={prevSuggestions}
            suggestions={suggestions && suggestions.length ? suggestions : null}
            pending={loading}
            getSuggestionItemProps={this.getSuggestionItemProps}
            prevMapSearches={prevMapSearches}
          />
        </Overlay>

        <Overlay
          show={showBrowserGeolocationTooltip}
          target={() => this.geoLocationTootipTargetRef.current}
          placement="top"
        >
          <Tooltip id={name + '-geo-location-tooltip'}>{messages.click_to_start_geolocation}</Tooltip>
        </Overlay>
      </React.Fragment>
    );
  }
}

FormGooglePlacesAutocomplete.defaultProps = {
  enableBrowserLocation: false,
  autoBrowserLocation: false,
  placeholder: 'Type to start search',
  name: 'placesAutocomplete',
  prevMapSearches: [],
  onSelect: () => {},
};

export default legacyInjectIntl(FormGooglePlacesAutocomplete);
